import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { environment } from '@lmc-env/environment';
import { Store } from '@ngrx/store';
import { logout, selectUser, State } from '@lmc-app/store';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { User } from '@lmc-app/shared/interface/user.interface';
import { MatDialog } from '@angular/material/dialog';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentInfoDialogComponent } from './environment-info-dialog/environment-info-dialog.component';
import { CreateApplicationModalComponent } from './create-application-modal/create-application-modal.component';
import { SearchModalComponent } from './search-modal/search-modal.component';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import { ApplicationSearchService } from '@lmc-app/core/services/application-search.service';

@Component({
	selector: 'app-top-nav',
	templateUrl: './top-nav.component.html',
	styleUrls: ['./top-nav.component.scss'],
})
export class TopNavComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	
	// Permission flags
	protected collectionListComponentFlag = false;

	private pageHistoryArray: string[] = []; // Assign the response on this array

	private userName: string = "";

	protected appVersion = environment.version;
	protected environmentName = environment.environment;
	protected isNonProduction = environment.environment !== 'Production';
	private readonly _onDestroy = new Subject<void>();
	protected collectionManagerReportComponentFlag = false;
	protected ptpFinanceFlag: boolean = false;
	protected adminDashboardFlag: boolean = false;
	protected adminReportFlag: boolean = false;
	protected userRoleID!: number;
	protected insuranceCancellationFlag: boolean = false;
	protected loanServicingReportsFlag: boolean = false;

	protected isExternalView: boolean = false;
	protected fullName: string = "";

	protected globalSearchParam: string = "";
	protected searchKeyword: string = "";

	protected financeNlsSettingFlag: boolean = false;

	protected isExternalCollector: boolean = false;

	constructor(
		private apiService: LegacyApiService,
		private store: Store<State>,
		private dialog: MatDialog,
		private afMessaging: AngularFireMessaging,
		private activatedRoute: ActivatedRoute,
		private _listenerService: ListenerService, 
		private _appSearchService: ApplicationSearchService,
	) { }

	public ngOnInit(): void {
		// Subscribe to user changes from NgRx store
		// This replaces the old loadVerifySignin() which called verifysession API
		this.parentSub.add(this.store
			.select(selectUser)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User | null) => {
				if (user) {
					// User is authenticated, populate component properties
					this.userName = user.email;
					this.collectionListComponentFlag = user.permissions?.collection_list ?? false;
					this.collectionManagerReportComponentFlag =
						user.permissions?.collection_manager_reports ?? false;
					this.ptpFinanceFlag = user.permissions?.ptp_finance ?? false;
					this.adminDashboardFlag = user.permissions?.admin_dashboard ?? false;
					this.adminReportFlag = user.permissions?.admin_report ?? false;
					this.insuranceCancellationFlag = user.permissions?.insurance_cancellation ?? false;
					this.financeNlsSettingFlag = user.permissions?.finance_nls_settings ?? false;
					this.loanServicingReportsFlag = user.permissions?.loan_servicing_reports ?? false;
					this.userRoleID = user.roleID;
					this.fullName = user.full_name;
					this.isExternalView = user.roleID === 12;
					this.isExternalCollector = user.roleID === 13;

					this.loadPageHistory();
				}
			}));

		this.parentSub.add(this.activatedRoute?.queryParams?.subscribe((queryParams) => {
			this.globalSearchParam = queryParams?.['searchParam'];
			this.searchKeyword = queryParams?.['searchKeyword'];
		}));

		this.parentSub.add(
			this._listenerService.on<boolean>('clearedPageHistories').subscribe((cleared) => {   
				if (cleared) {
					this.pageHistoryArray = [];
				}
			})
		);
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	protected logout(): void {
		// Dispatch logout action to NgRx store
		// This will:
		// 1. Call backend logout API (revoke tokens, clear cookies)
		// 2. Clear user state
		// 3. Clear permissions state
		// 4. Navigate to /login
		this.store.dispatch(logout());
	}

	// Load the page history on the dropdown
	private loadPageHistory(): void {
		this.parentSub.add(this._appSearchService.getPageHistory(this.userName).subscribe((res) => {
			this.pageHistoryArray = res.data;
		}));
	}

	protected onSearchClicked(evt: MouseEvent): void {
		const trigger = new ElementRef(evt.target);
		this.dialog.open(SearchModalComponent, {
			autoFocus: true,
			enterAnimationDuration: 0,
			exitAnimationDuration: 0,
			data: {
				globalSearchParam: this.globalSearchParam,
				searchKeyword: this.searchKeyword,
				pageHistoryArray: this.pageHistoryArray,
				trigger,
				userEmail: this.userName
			},
		});
	}

	protected openCreateApplicationModal(): void {
		this.dialog.open(CreateApplicationModalComponent, {
			width: '80vw',
		});
	}

	protected async openEnvironmentInfoModal(): Promise<void> {
		// Get notification permission status
		const notificationPermission = 'Notification' in window
			? Notification.permission
			: 'Not supported';

		// Get FCM token
		let fcmToken = 'N/A';
		try {
			if (notificationPermission === 'granted') {
				const token = await this.afMessaging.getToken.toPromise();
				fcmToken = token || 'Token not available';
			} else {
				fcmToken = 'Permission not granted';
			}
		} catch (error) {
			console.error('Error getting FCM token:', error);
			fcmToken = 'Error getting token';
		}

		// Prepare environment info
		const envInfo = {
			version: environment.version,
			environment: environment.environment,
			notificationPermission: notificationPermission,
			fcmToken: fcmToken,
		};

		// Open dialog with environment info
		this.dialog.open(EnvironmentInfoDialogComponent, {
			width: '600px',
			data: envInfo
		});
	}
}